import {
  StFooterContainer,
  StPrimaryLinkContainer,
  StSecondaryLinkContainer,
  StSocialMediaContainer,
  StCopyRightText,
  StDisclaimerText,
  StFooterWrapper,
  StFooterIconContainer,
  StSocialMediaDefaultContainer,
  StSecondaryLinkDefaultContainer,
  StDefaultCopyRightText,
  StDefaultDisclaimerText,
} from './Footer.style';
import {
  RichText,
  NextImage,
  Field,
  LinkField,
  ImageField,
  ImageFieldValue,
} from '@sitecore-jss/sitecore-jss-nextjs';
import Grid from '@mui/material/Grid';
import PrimaryLinks from '../shared/PrimaryLinks/PrimaryLinks';
import SecondaryLinks from '../shared/SecondaryLinks/SecondaryLinks';
import SocialMediaLinks from '../shared/SocialMediaLinks/SocialMediaLinks';
import { ComponentProps } from 'lib/component-props';
import { Fragment, useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import Link from 'next/link';

interface ISecondaryLinkItem {
  id: string;
  url: string;
  name: string;
  displayName: string;
  fields: {
    image: ImageField;
    link: LinkField;
  };
}

interface ISocialMediaLinkItem {
  id: string;
  url: string;
  name: string;
  displayName: string;
  fields: {
    image: ImageField;
    link: LinkField;
  };
}
type FooterProps = ComponentProps & {
  fields: {
    sponsorLogo?: ImageField;
    logo?: ImageField;
    copyrightText: Field<string>;
    primaryLinks?: Array<LinkField>;
    secondaryLinks: Array<ISecondaryLinkItem>;
    socialMediaIconList: Array<ISocialMediaLinkItem> | [];
    disclaimerText?: Field<string>;
    bgColor?: Field<string>;
    lineColor?: Field<string>;
    fontColor?: Field<string>;
    hoverColor?: Field<string>;
    iconColor?: Field<string>;
    LinkColor?: Field<string>;
  };
};

const Footer = (props: FooterProps): JSX.Element => {
  const theme = useTheme();
  const [copyRightText, setCopyRightText] = useState<any>(props?.fields?.copyrightText?.value);
  useEffect(() => {
    const fullYear = new Date().getFullYear();
    const newString = props?.fields?.copyrightText?.value
      ?.replace(/\[currentYear\]/g, `${fullYear}`)
      ?.replace(/\[copyrightyear\]/g, `${fullYear}`);

    setCopyRightText(newString as any);
  }, [props?.fields?.copyrightText]);

  useEffect(() => {
    let resizeTimeout: NodeJS.Timeout;

    const positionCookieIcon = () => {
      const footerElement = document.getElementById('footer');
      const cookieIconElement = document.getElementById('ot-sdk-btn-floating');

      if (footerElement && cookieIconElement) {
        const footerRect = footerElement.getBoundingClientRect();
        const windowHeight = window.innerHeight;

        if (footerRect.top < windowHeight) {
          const distanceFromBottom = windowHeight - footerRect.top;
          cookieIconElement.style.setProperty(
            'bottom',
            `${distanceFromBottom + 14}px`,
            'important'
          );
        } else {
          cookieIconElement.style.removeProperty('bottom');
        }
      }
    };

    const handleScroll = () => {
      positionCookieIcon();
    };

    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        positionCookieIcon();
      }, 100);
    };

    positionCookieIcon();

    window.addEventListener('resize', handleResize);
    window.addEventListener('load', positionCookieIcon);
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('load', positionCookieIcon);
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(resizeTimeout);
    };
  }, []);

  if (!props?.fields) return <></>;
  const primaryLinks =
    props?.fields?.primaryLinks?.map(({ name, fields }: any) => ({
      name,
      link: fields.link,
    })) || [];
  const secondaryLinks =
    props?.fields?.secondaryLinks?.map(({ name, fields }: any) => ({
      name,
      link: fields.link,
      image: fields.image,
    })) || [];
  const isCRApp = (props as any)?.params?.footerVariantFlag === 'CR' || false;
  const isDefaultApp = (props as any)?.params?.footerVariantFlag === 'Default' || false; //Avacare
  const socialMediaIconsLength = props?.fields?.socialMediaIconList?.length || 0;
  const linkColor = props?.fields?.LinkColor?.value || (theme.palette as any).fontTextColor1.main;

  return (
    <StFooterWrapper
      id="footer"
      className="ui-footer"
      lineColor={props?.fields?.lineColor?.value}
      bgColor={props?.fields?.bgColor?.value}
      isDefaultApp={isDefaultApp}
    >
      <StFooterContainer
        container
        component="footer"
        isDefaultApp={isDefaultApp}
        sx={{
          color: props?.fields?.fontColor?.value || (theme.palette as any).fontTextColor1.main,
          paddingBottom: isDefaultApp ? '0rem' : 'none',
        }}
      >
        <StPrimaryLinkContainer item container xs={12} md={12} lg={8}>
          {isCRApp || isDefaultApp ? (
            <PrimaryLinks
              primaryLinks={primaryLinks}
              linkFontColor={linkColor}
              isDefaultApp={isDefaultApp}
            />
          ) : (
            props?.fields?.sponsorLogo?.value?.src && (
              <Grid item id="footer-logo">
                <NextImage field={props?.fields?.sponsorLogo as ImageFieldValue} />
              </Grid>
            )
          )}
        </StPrimaryLinkContainer>
        {isDefaultApp && (
          <Fragment>
            {socialMediaIconsLength > 0 && (
              <StSocialMediaDefaultContainer item container xs={12} md={12} lg={4}>
                <SocialMediaLinks
                  socialMediaIconList={props?.fields?.socialMediaIconList}
                  hoverColor={props?.fields?.hoverColor?.value}
                  iconColor={props?.fields?.iconColor?.value}
                />
              </StSocialMediaDefaultContainer>
            )}
          </Fragment>
        )}

        {isCRApp && (
          <Fragment>
            {socialMediaIconsLength > 0 && (
              <StSocialMediaContainer item container xs={12} md={12} lg={4}>
                <SocialMediaLinks
                  socialMediaIconList={props?.fields?.socialMediaIconList}
                  hoverColor={props?.fields?.hoverColor?.value}
                  iconColor={props?.fields?.iconColor?.value}
                />
              </StSocialMediaContainer>
            )}
          </Fragment>
        )}
        {!isDefaultApp && (
          <Fragment>
            <StSecondaryLinkContainer item container xs={12} md={12} lg={8}>
              <SecondaryLinks
                secondaryLinks={secondaryLinks}
                linkFontColor={linkColor}
                hoverColor={props?.fields?.hoverColor?.value}
              />
            </StSecondaryLinkContainer>
            <StCopyRightText item container xs={12} md={12} lg={4}>
              <RichText field={{ ...props?.fields?.copyrightText, value: copyRightText }} />
            </StCopyRightText>
          </Fragment>
        )}
        {isDefaultApp && (
          <Fragment>
            <StSecondaryLinkDefaultContainer item container xs={12}>
              <SecondaryLinks
                secondaryLinks={secondaryLinks}
                linkFontColor={linkColor}
                hoverColor={props?.fields?.hoverColor?.value}
                isDefaultApp={isDefaultApp}
              />
            </StSecondaryLinkDefaultContainer>

            {props?.fields?.disclaimerText && (
              <StDefaultDisclaimerText>
                <RichText field={props?.fields?.disclaimerText} />
              </StDefaultDisclaimerText>
            )}

            <StFooterIconContainer item container xs={12} md={12} lg={4} margin={0}>
              <Grid item id="footer-logo">
                <Link passHref href="/">
                  <NextImage field={props?.fields?.logo as ImageFieldValue} />
                </Link>
              </Grid>
            </StFooterIconContainer>

            <StDefaultCopyRightText item container xs={12} md={12} lg={8}>
              <RichText field={{ ...props?.fields?.copyrightText, value: copyRightText }} />
            </StDefaultCopyRightText>
          </Fragment>
        )}
        {!isDefaultApp && props?.fields?.disclaimerText && (
          <StDisclaimerText>
            <RichText field={props.fields.disclaimerText} />
          </StDisclaimerText>
        )}
      </StFooterContainer>
    </StFooterWrapper>
  );
};

export default Footer;
